import { TourInfo, Mote, MoteType, Media, SectionColor, InfoActionType, InfoSectionType, MediaType, MotesPaths, PlanType, Voucher, ServiceId } from '../../models';
import { TourInfoTranslation } from './tour-info.translation';

export class TourInfoAutogen {

    public static generateTourInfo(tour: Mote, storyCount: number, hasChallenge: boolean, hasSound: boolean, creatorPlan: PlanType, userVoucher: Voucher = null, serviceId: ServiceId = null): TourInfo {
        if(!tour) {
            console.warn("Tour does not exist, can not auto generate TourInfo");
            return null;
        }

        let tourTranslation = new TourInfoTranslation(tour.language, tour.duration, tour.length, tour.startPlace, tour.endPlace, storyCount, tour.city);
        const sneakPeekAction = tour.sneakPeekMediaArray?.length <= 0 ? {} : { "label": tourTranslation.getText('seeSneakPeek'), "type": InfoActionType.SCROLL_TO_SNEAK_PEEK, "style": 'button-outline' };

        const tourInfoJson = {
            "title": tour.title,
            "tourKey": tour.key,
            "sections": {},
            "language": tour.language,
            "keywords": tour.tags,
            "description": tour.previewText,
            "duration": tour.duration,
            "type": tour.type,
            "price": tour.price,
            "length": tour.length,
            "storyCountLabel": storyCount + ' ' + tourTranslation.getText('stories'),
            "priceLabel": tour.price ? tour.price + ' DKK' : tourTranslation.getText('free'),
            "startPlace": tour.startPlace,
            "userVoucher": userVoucher,
            "media": {
                "type": MediaType.IMAGE,
                "hd": tour.image.hd.url,
                "hdFallback": tour.image.hd.fallback,
                "thumb": tour.image.thumb.url,
                "thumbFallback": tour.image.thumb.fallback,
                "title": tour.title + ' ' + $localize`cover image.`
            },
        };

        // Tour presentation section
        tourInfoJson.sections[tour.key + '_sectionA'] = {
            "title": tour.title,
            "paragraphs": [
                tour.previewText ? tour.previewText : tourTranslation.getText('subtitle'),
            ],
            "actions": [{ "type": InfoActionType.START_TOUR }, sneakPeekAction ],
            "color": SectionColor.BLACK,
            "type": InfoSectionType.SPLIT_MEDIA_RIGHT,
            "media": {
                "type": tour.image.video.url ? MediaType.VIDEO : MediaType.IMAGE,
                "hd": tour.image.hd.url,
                "hdFallback": tour.image.hd.fallback,
                "thumb": tour.image.thumb.url,
                "thumbFallback": tour.image.thumb.fallback,
                "video": tour.image.video.url,
                "title": tour.title + ' ' + $localize`cover image.`
            },
            "wrapReverse": true,
            "key": tour.key + '_sectionA'
        };

        // Tour description section
        tourInfoJson.sections[tour.key + '_sectionB'] = {
            "title": tourTranslation.getText('tourDescTitle'),
            "paragraphs": [
                ...tour.text.split("\n").filter(val => val != "")
            ],
            "creatorParagraph":
                creatorPlan == PlanType.PERSONAL ? tourTranslation.getText('creatorPersonal') :
                creatorPlan == PlanType.PROFESSIONAL ? tourTranslation.getText('creatorProfessional') :
                creatorPlan == PlanType.INTERNAL ? tourTranslation.getText('creatorInternal') :
                tourTranslation.getText('creatorEnterprise'),
            "color": SectionColor.WHITE,
            "media": {
                "hd": tour.mediaArray.length > 1 && tour.mediaArray[1]?.hd?.url ? tour.mediaArray[1].hd.url : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds_webp%2Ftour_info_desc_default.webp?alt=media&token=11c44a41-b1c2-487b-96ac-9e52ff905bf5",
                "hdFallback": tour.mediaArray.length > 1 && tour.mediaArray[1]?.hd?.fallback ? tour.mediaArray[1].hd.fallback : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds%2Ftour-description-default.jpg?alt=media&token=7f5d29e7-9985-479f-92f9-79c2322fc337",
                "thumb": tour.mediaArray.length > 1 && tour.mediaArray[1]?.thumb?.url ? tour.mediaArray[1].thumb.url : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds_webp%2Ftour_info_desc_default_thumb.webp?alt=media&token=93ecf8e2-8dfe-4ea0-999f-23ef11cab066",
                "thumbFallback": tour.mediaArray.length > 1 && tour.mediaArray[1]?.thumb?.fallback ? tour.mediaArray[1].thumb.fallback : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds%2Ftour-description-default_thumb.jpg?alt=media&token=41ef84ce-c519-44cd-8009-c1fc7bede2be",
                "type": MediaType.IMAGE,
                "title": tour.mediaArray.length > 1 ? $localize`Image from the tour.` : $localize`Friends standing in front of each other, phones in their hands and earphones in, surrounded by floating padlocks.`
            },
            "type": InfoSectionType.SPLIT_MEDIA_LEFT,
            "wrapReverse": true,
            "key": tour.key + '_sectionB'
        };

        // How-does-is-work section
        tourInfoJson.sections[tour.key + '_sectionC'] = {
            "title": tourTranslation.getText('workTitle'),
            "paragraphs": [
                tourTranslation.getText('workLocation'),
                tour.type == MoteType.ROUTE ? tourTranslation.getText('workRoute') : tour.type == MoteType.COLLECTION ? tourTranslation.getText('workCollection') : '',
                tourTranslation.getText('workUnlock'),
                hasSound ? tourTranslation.getText('workPodcast') : '',
                hasChallenge ? tourTranslation.getText('workChallenge') : ''
            ],
            "icons": [
                "mobile",
                "route",
                "place",
                hasSound ? "headphones" : '',
                hasChallenge ? "medal-solid" : ''
            ],
            "color": SectionColor.YELLOW,
            "media": {
                "hd": tour.mediaArray.length > 2 && tour.mediaArray[2]?.hd?.url ? tour.mediaArray[2].hd.url : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds_webp%2Ftour_info_how_default.webp?alt=media&token=0c73ff77-f383-4ccf-9c20-a25093bf3a90",
                "hdFallback": tour.mediaArray.length > 2 && tour.mediaArray[2]?.hd?.fallback ? tour.mediaArray[2].hd.fallback : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds%2Fhow-does-it-work-5.jpg?alt=media&token=13f2b21d-b242-46aa-bca0-903fccc4a51b",
                "thumb": tour.mediaArray.length > 2 && tour.mediaArray[2]?.thumb?.url ? tour.mediaArray[2].thumb.url : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds_webp%2Ftour_info_how_default_thumb.webp?alt=media&token=a3a6fdaa-6dac-469b-a3ec-8bc65ca466d7",
                "thumbFallback": tour.mediaArray.length > 2 && tour.mediaArray[2]?.thumb?.fallback ? tour.mediaArray[2].thumb.fallback : "https://firebasestorage.googleapis.com/v0/b/motesdev.appspot.com/o/static-images%2Fbackgrounds%2Fhow-does-it-work-5_thumb.jpg?alt=media&token=975fc544-55f0-4fdd-8206-43b69fc0fb96",
                "type": MediaType.IMAGE,
                "title": tour.mediaArray.length > 2 ? $localize`Image from the tour.` : $localize`Hands holding a phone - the screen showing the StoryHunt map with routes and stories.`
            },
            "type": InfoSectionType.SPLIT_MEDIA_RIGHT,
            "key": tour.key + '_sectionC'
        };

        // Map section
        tourInfoJson.sections[tour.key + '_sectionD'] = {
            "type": InfoSectionType.MAP,
            "color": SectionColor.BLACK,
            "actions": [{
                "label": tourTranslation.getText('seeRoute'),
                "type": InfoActionType.OPEN_MAP,
                "style": 'button-filled'
            }],
            "mediaRedirect": MotesPaths.STORY_MAP(tour.key),
            "key": tour.key + '_sectionD'
        };

        // Generate sneak peek section
        if(tour.sneakPeekMediaArray?.length > 0) tourInfoJson.sections[tour.key + '_sectionE'] = this.generateSneakPeekSection(tourTranslation, tour);

        // Generate open-app section
        if(!tour.productLocked) tourInfoJson.sections[tour.key + '_sectionF'] = this.generateOpenAppSection(tourTranslation, tour, serviceId);

        // Generate products section
        let productKeys = Object.keys(tour.products);
        // Generate product section G if any product are associated with this tour
        if(tour.productLocked && (productKeys.length > 0)) tourInfoJson.sections[tour.key + '_sectionG'] = this.generateProductSection(tourTranslation, tour);

        // FAQ section
        tourInfoJson.sections[tour.key + '_sectionH'] = {
            "type": InfoSectionType.CENTER,
            "color": SectionColor.YELLOW,
            "title": tourTranslation.getText('faq'),
            "expandableBoxes": [
                { title: tourTranslation.getText('faqStartPlaceQ'), content: (tour.type == MoteType.COLLECTION) ? tourTranslation.getText('faqStartPlaceCollectionA') : tourTranslation.getText('faqStartPlaceRouteA') },
                { title: tourTranslation.getText('faqDurationQ'), content: (tour.type == MoteType.COLLECTION) ? tourTranslation.getText('faqDurationCollectionA') : tourTranslation.getText('faqDurationRouteA') },
                { title: (tour.price > 0) ? tourTranslation.getText('faqGiftQ') : tourTranslation.getText('faqFreeQ'), content: (tour.price > 0) ? tourTranslation.getText('faqGiftA') : tourTranslation.getText('faqFreeA') },
                { title: tourTranslation.getText('faqFrequencyQ'), content: (tour.price > 0 ? tourTranslation.getText('faqFrequencyA') : tourTranslation.getText('faqFrequencyFreeA')) },
                { title: tourTranslation.getText('faqFriendsQ'), content: (hasChallenge) ? tourTranslation.getText('faqFriendsWithQuizA') : tourTranslation.getText('faqFriendsA') },
                { title: tourTranslation.getText('faqStartTimeQ'), content: tourTranslation.getText('faqStartTimeA') }
            ],
            "key": tour.key + '_sectionH',
        }

        // Create tour info object
        let tourInfo = new TourInfo(tour.key, tourInfoJson);
        // Set products in tour info to products from tour
        tourInfo.products = tour.products;

        return tourInfo;
    }

    private static modifyMedia(media: Media): { "type": string, "hd": string, "hdFallback": string, "thumb": string, "thumbFallback": string, "video": string, "sound": string, "description": string } {
        return {
            "type": media.type,
            "hd": media.hd.url,
            "hdFallback": media.hd.fallback,
            "thumb": media.thumb.url,
            "thumbFallback": media.thumb.fallback,
            "video": media.video.url,
            "description": media.description,
            "sound": media.sound.url
        }
    }

    private static generateSneakPeekSection(tourTranslation: TourInfoTranslation, tour: Mote) {
        return {
            "actions": [{ type: "SHOW_TRAILER" }],
            "color": SectionColor.WHITE,
            "mediaArray": tour.sneakPeekMediaArray.map((media: Media) => this.modifyMedia(media)),
            "paragraphs": [tour.sneakPeekText],
            "title": tour.sneakPeekHeader ? tour.sneakPeekHeader : tourTranslation.getText('sneakPeekHeader'),
            "type": InfoSectionType.CENTER,
            "key": tour.key + '_sectionE',
        }
    }

    private static generateOpenAppSection(tourTranslation: TourInfoTranslation, tour: Mote, serviceId: ServiceId) {
        return {
            "title": tourTranslation.getText('getStarted'),
            "paragraphs": [
                (serviceId == ServiceId.WEB_APP || serviceId == ServiceId.NATIVE_APP) ? tourTranslation.getText('explore') : tourTranslation.getText('visit'),
            ],
            "type": InfoSectionType.CENTER,
            "color": SectionColor.BLACK,
            "actions": [{ "type": InfoActionType.START_TOUR }],
            "key": tour.key + '_sectionF'
        };
    }

    private static generateProductSection(tourTranslation: TourInfoTranslation, tour: Mote) {
        return {
            "title": tourTranslation.getText('buyTour'),
            "paragraphs": [
                tourTranslation.getText('buyUseAgain'),
                tourTranslation.getText('buyShare'),
                tourTranslation.getText('buyBring'),
            ],
            "icons": [
                "ticket-alt",
                "group",
                "headphones"
            ],
            "color": SectionColor.BLACK,
            "type": InfoSectionType.CENTER,
            "actions": [{
                "label": tourTranslation.getText('buyTour'),
                "type": InfoActionType.BUY,
                "style": 'button-filled'
            }],
            "showProducts": true,
            "key": tour.key + '_sectionG'
        }
    }
}
